import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import { userService } from "../../../_services";
import FilterTagCom from '../FilterTag.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Download, BarChart3, List } from "lucide-react";

// Import modernized widgets
import BarWidget from '../BarWidget';
import ColumnPieWidget from '../ColumnPieWidget';
import MultiLineWidget from '../MultiLineWidget';
import ClaimWidget from '../Claim';
import TableWidget from '../TableWidget';
import MultiStackBarWidget from '../MultiStackBarWidget';

/**
 * Admin Dashboard Component
 * Modernized with React hooks - Displays widgets for admin analytics
 */
function Dashboard({ startDate: propStartDate, endDate: propEndDate, dist_usertype, handeChangefilter, dashboardName: propDashboardName, filterKey }) {
    // Initialize dates
    const getInitialStartDate = () => {
        const stored = localStorage.getItem('User_Start_Date');
        if (stored) return moment(stored).format("YYYYMMDD");
        return moment(new Date().setDate(new Date().getDate() - 30)).format("YYYYMMDD");
    };

    const getInitialEndDate = () => {
        const stored = localStorage.getItem('User_End_Date');
        return stored ? moment(stored).format("YYYYMMDD") : moment(new Date()).format("YYYYMMDD");
    };

    // State
    const [startDate, setStartDate] = useState(getInitialStartDate());
    const [endDate, setEndDate] = useState(getInitialEndDate());
    const [graphData, setGraphData] = useState([]);
    const [filterTagList, setFilterTagList] = useState('');
    const [legendValues, setLegendValues] = useState({});
    const [activeModes, setActiveModes] = useState({});
    const [loading, setLoading] = useState(false);
    const [widgetHasData, setWidgetHasData] = useState({});
    const exportHandlersRef = useRef({});
    
    const roleName = 'Admin';
    const whId = '61';
    const roleType = 'Admin';

    // Update dates when props change
    useEffect(() => {
        if (propStartDate && propEndDate) {
            const newStartDate = moment(propStartDate).format("YYYYMMDD");
            const newEndDate = moment(propEndDate).format("YYYYMMDD");
            
            if (startDate !== newStartDate || endDate !== newEndDate) {
                setStartDate(newStartDate);
                setEndDate(newEndDate);
                changeData(newStartDate, newEndDate);
            }
        }
    }, [propStartDate, propEndDate]);

    // Re-run changeData whenever the parent signals a filter/tab/search change via filterKey.
    // This replaces the fragile counter-based Prev_HD_Type / Filter_Last_Update_Time comparison
    // that the old class-component componentDidUpdate relied on.
    useEffect(() => {
        if (filterKey === undefined) return; // no-op when prop is absent
        changeData();
    }, [filterKey]);

    // Initial data load
    useEffect(() => {
        localStorage.setItem('Filter_Last_Update_Time', new Date());
        changeData();
    }, []);

    const handleChangeA = (e) => {
        if (handeChangefilter) {
            handeChangefilter(e);
        }
    };

    // Called by each widget when it loads legend data (number shown before title)
    const handleLegendUpdate = useCallback((widgetId, value) => {
        setLegendValues(prev => ({ ...prev, [widgetId]: value }));
    }, []);

    // Called by each widget when its data is ready — registers export function
    const makeExportReady = useCallback((widgetId) => (fn) => {
        exportHandlersRef.current[widgetId] = fn;
    }, []);

    // Called by each widget after loading — reports whether it has actual data
    const makeDataReady = useCallback((widgetId) => (hasData) => {
        setWidgetHasData(prev => ({ ...prev, [widgetId]: hasData }));
    }, []);

    const handleModeChange = useCallback((widgetId, mode) => {
        setActiveModes(prev => ({ ...prev, [widgetId]: mode }));
    }, []);

    const changeData = (newStartDate, newEndDate) => {
        setLoading(true);
        setLegendValues({});
        const APIBaseURL = '/api/ws';
        const startDateStr = newStartDate || startDate;
        const endDateStr = newEndDate || endDate;

        // Parse advanced filter
        let AdFilterJson = '';
        let stringAdFilterJson = '';
        try {
            AdFilterJson = JSON.parse(localStorage.getItem('Advance_Filter'));
            if (AdFilterJson != null && AdFilterJson != '' && AdFilterJson != undefined && AdFilterJson != "null") {
                stringAdFilterJson = JSON.stringify(AdFilterJson);
                setFilterTagList(stringAdFilterJson);
            } else {
                setFilterTagList('');
            }
        } catch (error) {
            setFilterTagList('');
        }

        // Build request JSONs
        const TotalTransactions_PostData_RequestJson = JSON.stringify({
            wname: "61",
            wholesaler_id: "61",
            start_date: startDateStr,
            end_date: endDateStr,
            rcount: 0,
            chart_type: "columnList",
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type')
        });

        const Gr_No_Transactions_PostData_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateStr,
            end_date: endDateStr,
            rcount: 30,
            chart_type: "columnList",
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        const Unique_Members_Widget_PostData_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateStr,
            end_date: endDateStr,
            rcount: 0,
            chart_type: "columnList",
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        const Drug_Transactions_Widget_PostData_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateStr,
            end_date: endDateStr,
            rcount: 30,
            chart_type: "pielist",
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        const New_Repeat_Widget_PostData_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateStr,
            end_date: endDateStr,
            rcount: 30,
            chart_type: "multilinelist",
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        const Claim_PostData_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateStr,
            end_date: endDateStr,
            rcount: 0,
            chart_type: "list",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        const Commission_MultiStack_Column_PostData_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateStr,
            end_date: endDateStr,
            rcount: 0,
            chart_type: "column_multiaxislist",
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type')
        });

        const PatientSaving_Widget_Postdata_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateStr,
            end_date: endDateStr,
            rcount: 0,
            chart_type: "columnList",
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        const Table_Pharmacy_RequestJson = JSON.stringify({
            wname: "61",
            start_date: startDateStr,
            end_date: endDateStr,
            rcount: 30,
            chart_type: "list",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: "61"
        });

        setWidgetHasData({});
        setGraphData([
            {
                url: APIBaseURL + "/total_transactions_new",
                type: "Bar",
                title: "Total Transactions",
                Subtitle: "Total number of transactions for each day",
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "# of Transactions",
                interval: 'day',
                id: "WTotalTransaction",
                RequestJson: TotalTransactions_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/RXSClaims",
                type: "Claim",
                title: "Claim Counts",
                Subtitle: "",
                id: "RxSensClaim",
                RequestJson: Claim_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/new_and_repeat_claims_Line",
                type: "MultiLine",
                title: "New vs Repeat Transactions",
                Subtitle: "New vs Repeat Compensable Transactions",
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "# of Transactions",
                interval: 'day',
                id: "WNewRepeatMultiLine",
                RequestJson: New_Repeat_Widget_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/Transactions_By_Group_Number",
                type: "Bar",
                title: "Transactions by Group Number",
                Subtitle: "Number of transactions for each group number",
                DataType: "GroupNumber",
                XlabelString: "Group Number",
                YlabelString: "# of Transactions",
                id: "WGroupNumberTransaction",
                RequestJson: Gr_No_Transactions_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/Total_PaitentSavings",
                type: "Bar",
                title: "Total Patient Savings ($)",
                Subtitle: "Total savings for all patients in $",
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "Total Savings",
                id: "WPatientSavings",
                RequestJson: PatientSaving_Widget_Postdata_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/Drug_Wise_Transaction",
                type: "ColumnPie",
                title: "Transactions by Drug",
                Subtitle: "Number of transactions for each drug",
                DataType: "DrugName",
                XlabelString: "Date",
                YlabelString: "# of Unique Member ID's",
                interval: 'day',
                id: "WDrugTransaction",
                RequestJson: Drug_Transactions_Widget_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/EarnedCommission",
                type: "MultiStackBar",
                title: "Total Revenue Earned",
                Subtitle: "Revenue earned for each month",
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "Commission",
                interval: 'month',
                id: "WEarnedCommission",
                RequestJson: Commission_MultiStack_Column_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/prescribe_transaction",
                type: "Table",
                title: "Transactions by Prescriber",
                Subtitle: "Number of transactions at each Prescriber",
                ColorCode: "#ffba10",
                XlabelString: "",
                YlabelString: "",
                DataType: "Prescriber",
                id: "WPrescriberTransaction",
                RequestJson: Table_Pharmacy_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/Unique_Members_Widget",
                type: "Bar",
                title: "Unique Member ID's",
                Subtitle: "Number of unique members transacted on each day",
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "# of Unique Member ID's",
                interval: 'day',
                id: "WUniqueMembersWidget",
                RequestJson: Unique_Members_Widget_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            }
        ]);
        setLoading(false);
    };

    // Get widget layout classes
    const getWidgetClasses = (widgetId) => {
        const layoutMap = {
            'WTotalTransaction': 'col-span-12',
            'RxSensClaim': 'col-span-12',
            'WNewRepeatMultiLine': 'col-span-12',
            'WGroupNumberTransaction': 'col-span-12',
            'WPatientSavings': 'col-span-12',
            'WDrugTransaction': 'col-span-12',
            'WEarnedCommission': 'col-span-12',
            'WPrescriberTransaction': 'col-span-12',
            'WUniqueMembersWidget': 'col-span-12'
        };

        return layoutMap[widgetId] || 'col-span-12';
    };

    // Widget types that support chart/table toggle
    const DUAL_MODE_TYPES = new Set(['Bar', 'MultiLine', 'MultiStackBar', 'ColumnPie']);

    // Map widget type → readable chart label for tooltip
    const getChartLabel = (type) => {
        const map = { Bar: 'Bar', MultiLine: 'Line', MultiStackBar: 'Bar', ColumnPie: 'Pie' };
        return map[type] || 'Chart';
    };

    // Render widget based on type
    const renderWidget = (widget, index) => {
        const commonProps = {
            data: widget,
            _Index: index,
            handlechangea: handleChangeA,
            onLegendUpdate: handleLegendUpdate,
            onExportReady: makeExportReady(widget.id),
            onDataReady: makeDataReady(widget.id),
            mode: activeModes[widget.id] || 'chart'
        };
        const key = `widget-${widget.id}-${index}`;

        switch (widget.type) {
            case 'Bar':
                return <BarWidget key={key} {...commonProps} />;
            case 'ColumnPie':
                return <ColumnPieWidget key={key} {...commonProps} />;
            case 'MultiLine':
                return <MultiLineWidget key={key} {...commonProps} />;
            case 'Claim':
                return <ClaimWidget key={key} {...commonProps} />;
            case 'Table':
                return <TableWidget key={key} {...commonProps} />;
            case 'MultiStackBar':
                return <MultiStackBarWidget key={key} {...commonProps} />;
            default:
                return null;
        }
    };

    return (
        <div className="w-full space-y-6">
            {/* Filter Tags */}
            <FilterTagCom 
                Rolename={roleName}
                wh_Id={whId}
                Roletype={roleType}
                df={startDate}
                dt={endDate}
                handlechangea={handleChangeA}
                FilterTags={filterTagList}
                Loading={true}
            />

            {/* Widgets Grid */}
            <div className="grid grid-cols-12 gap-6">
                {graphData.map((widget, index) => {
                    // Special handling for Claim widget - no parent card
                    if (widget.type === 'Claim') {
                        return (
                            <div 
                                key={`widget-${widget.id}-${index}`}
                                className={getWidgetClasses(widget.id)}
                            >
                                <ClaimWidget 
                                    data={widget}
                                    key={`${widget.type}-${index}`}
                                    _Index={index}
                                    handlechangea={handleChangeA}
                                />
                            </div>
                        );
                    }
                    
                    // Regular widgets with card wrapper
                    return (
                        <Card 
                            key={`widget-${widget.id}-${index}`}
                            className={getWidgetClasses(widget.id)}
                        >
                            <CardHeader className="border-b px-6 py-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <CardTitle className="text-lg font-semibold text-primary flex items-center gap-2 flex-wrap">
                                            {!loading && legendValues[widget.id] && (
                                                <span className="text-accent font-bold">
                                                    {legendValues[widget.id]}
                                                </span>
                                            )}
                                            {widget.title}
                                        </CardTitle>
                                        {widget.Subtitle && (
                                            <CardDescription className="text-sm text-accent mt-1">
                                                {widget.Subtitle}
                                            </CardDescription>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {/* Export button — before toggle */}
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-8 w-8 text-primary border-primary/20 hover:bg-muted"
                                                        disabled={widgetHasData[widget.id] !== true}
                                                        onClick={() => exportHandlersRef.current[widget.id]?.()}
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>Download Report</TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                        {/* Chart / List toggle — only for dual-mode widgets */}
                                        {DUAL_MODE_TYPES.has(widget.type) && (
                                            <TooltipProvider>
                                                <div className={`flex items-center rounded-md bg-muted p-1 gap-0.5 ${widgetHasData[widget.id] !== true ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className={`h-7 w-7 transition-colors ${(activeModes[widget.id] || 'chart') === 'chart' ? 'bg-accent text-white hover:bg-accent hover:text-white' : 'text-primary hover:bg-transparent hover:text-primary'}`}
                                                                onClick={() => handleModeChange(widget.id, 'chart')}
                                                            >
                                                                <BarChart3 className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>{getChartLabel(widget.type)}</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className={`h-7 w-7 transition-colors ${(activeModes[widget.id] || 'chart') === 'table' ? 'bg-accent text-white hover:bg-accent hover:text-white' : 'text-primary hover:bg-transparent hover:text-primary'}`}
                                                                onClick={() => handleModeChange(widget.id, 'table')}
                                                            >
                                                                <List className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>List</TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </TooltipProvider>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-6 overflow-hidden">
                                {renderWidget(widget, index)}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}

export default Dashboard;
